import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  Check,
  ClipboardList,
  Clock,
  Download,
  FileText,
  Info,
  ListChecks,
  MapPin,
  Printer,
  Type,
} from "lucide-react";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { FieldLabel } from "../ui/FieldLabel";
import {
  downloadPdf,
  formatPackagesPDFDownload,
  hasValidReceiverPhone,
} from "../../api/downloadPdf";
import { generatePickingPackingForBatch } from "../../api/pickingPacking";
import { getAllPackages, updatePackagesStatus } from "../../api/packages";
import { getRegions, getCommunes } from "../../api/contracts";
import { multipleDownloadZPLFile } from "../../utils/downloadZebra";
import { useCountry } from "../../context/CountryContext";
import { showToast } from "../../utils/showToast";
import { apiBodyMessage, apiErrorMessage } from "../../utils/apiErrorMessage";
import useAuth from "../../hooks/useAuth";
import fileName from "../../utils/fileName";
import Axios from "../../config/axios-config";

// ─────────────────────────────────────────────────────────────────────────────
// Modal unificado de descarga de etiquetas (reemplaza al par de acciones
// "Etiquetas" [dropdown de formato] + "Masiva" [drawer lateral de pendientes]).
//
// Wizard de dos pasos:
//   1. Origen del lote — "Seleccionados en la tabla" (checkedPackage del
//      caller) o "Pendientes por rango y zona" (la vieja descarga masiva, que
//      busca status 0 por fechas/región/comuna y los marca como impresos).
//   2. Opciones — formato multi-selección (PDF hoja / térmica ZPL / TXT) y
//      hoja de picking & packing opcional, más rango/zona cuando el origen es
//      pendientes.
//
// Comportamiento heredado que se preserva a propósito:
//   - El PDF (ambos orígenes) marca los paquetes como impresos (status 100):
//     lo hace downloadPdf/updatePackagesStatus, igual que antes. La UI lo dice.
//   - ZPL y TXT comparten el mismo contenido térmico; cambia la extensión.
//   - El lote del picking & packing es el MISMO que salió en las etiquetas
//     (con el filtro de teléfono válido cuando el formato lo aplica) y jamás
//     rompe el flujo de etiquetas: generatePickingPackingForBatch es contenida.
//   - En pendientes, el clientId del PDF es user._id y el lote térmico filtra
//     por teléfono válido (paridad con el drawer que reemplaza).
// ─────────────────────────────────────────────────────────────────────────────

// HH (24h): con hh (12h sin AM/PM) cualquier hora PM se reescribía a AM y el
// rango de la descarga masiva se corría 12 horas (bug heredado del drawer viejo).
const INPUT_DATE_FORMAT = "YYYY-MM-DDTHH:mm";
const formatDateForInput = (date) => dayjs(date).format(INPUT_DATE_FORMAT);

const THERMAL_FORMATS = ["zpl", "txt"];

// Error whose message is already user-facing text (thrown by this module).
const userFacingError = (message) => Object.assign(new Error(message), { userFacing: true });

// Reason for the "marked as printed" notices: those templates already put their
// own period after {{motivo}}, so a trailing one from the backend is dropped.
const withoutTrailingPeriod = (text) => String(text).replace(/\.\s*$/, "");

const DownloadLabelsModal = ({
  isOpen,
  onClose,
  packages = [],
  statusDisabled = false,
  initialOrigin = null,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { countryId, loading: countryLoading } = useCountry();

  // Wizard
  const [step, setStep] = useState("origin"); // 'origin' | 'options'
  const [origin, setOrigin] = useState(null); // 'selected' | 'pending'

  // Opciones comunes
  const [formats, setFormats] = useState(["pdf"]);
  const [includePicking, setIncludePicking] = useState(true);

  // Pendientes: rango + zona
  const [startDateInput, setStartDateInput] = useState({});
  const [endDateInput, setEndDateInput] = useState({});
  const [errorTexts, setErrorTexts] = useState([]);
  const [interactedWith, setInteractedWith] = useState(false);
  const [regionsList, setRegionsList] = useState([]);
  const [communesList, setCommunesList] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState(false);

  // Ejecución
  const [isLoading, setIsLoading] = useState(false);
  const [processingStep, setProcessingStep] = useState("");
  const [labelsCount, setLabelsCount] = useState(null);

  const selectedCount = packages?.length || 0;

  // Reset completo en cada apertura. initialOrigin permite al CTA de /home
  // ("Seleccionar todo y descargar etiquetas") aterrizar directo en el paso 2.
  useEffect(() => {
    if (!isOpen) return;
    const startOrigin = initialOrigin === "selected" && selectedCount > 0 ? "selected" : null;
    setOrigin(startOrigin);
    setStep(startOrigin ? "options" : "origin");
    setFormats(startOrigin === "pending" ? ["pdf", "zpl"] : ["pdf"]);
    setIncludePicking(true);
    setStartDateInput({});
    setEndDateInput({ value: formatDateForInput(Date.now()) });
    setErrorTexts([]);
    setInteractedWith(false);
    // Region/commune/communesList intentionally persist across openings (parity
    // with the legacy always-mounted drawer): resetting them silently reverted
    // the pending batch to "all communes". If a reset is ever reintroduced, all
    // three must be cleared together — clearing communesList alone leaves a
    // selected commune with no matching <option>, an invisible active filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Regiones: solo cuando el modal está abierto (el drawer viejo las pedía al
  // montar la tabla, abriera o no el modal).
  useEffect(() => {
    if (!isOpen || countryLoading || !countryId) return;
    const fetchRegionsData = async () => {
      setIsLoadingRegions(true);
      try {
        const data = await getRegions({ countryId });
        if (!data.error) {
          setRegionsList(data.map((r) => ({ value: r.code, label: r.name })));
        }
      } catch (error) {
        console.error("Error al obtener regiones:", error);
        showToast(t("Error al obtener regiones"), false);
      } finally {
        setIsLoadingRegions(false);
      }
    };
    fetchRegionsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, countryId, countryLoading]);

  useEffect(() => {
    if (!selectedRegion || !countryId) return;
    const fetchCommunesData = async () => {
      setIsLoadingCommunes(true);
      try {
        const data = await getCommunes({ countryId, regionId: selectedRegion });
        if (!data.error) {
          setCommunesList(data.map((c) => ({ value: c.name, label: c.name })));
        }
      } catch (error) {
        console.error("Error al obtener comunas:", error);
        showToast(t("Error al obtener comunas"), false);
      } finally {
        setIsLoadingCommunes(false);
      }
    };
    fetchCommunesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, countryId]);

  // Nombre de la región seleccionada. El <select> guarda el `code` (lo pide el
  // fetch de comunas) pero el filtro del backend compara por nombre.
  const selectedRegionName =
    regionsList.find((region) => region.value === selectedRegion)?.label || null;

  const validateDates = useCallback(() => {
    const startDateParsed = dayjs(startDateInput?.value, INPUT_DATE_FORMAT);
    const endDateParsed = dayjs(endDateInput?.value, INPUT_DATE_FORMAT);
    const newErrorTexts = [];

    if (!(startDateInput?.value && endDateInput?.value)) {
      setStartDateInput((prev) => ({ ...prev, invalid: !prev?.value }));
      setEndDateInput((prev) => ({ ...prev, invalid: !prev?.value }));
      newErrorTexts.push(t("Debes seleccionar una fecha de inicio y una fecha de fin."));
      setErrorTexts(newErrorTexts);
      return false;
    }

    if (startDateParsed > endDateParsed) {
      newErrorTexts.push(t("La fecha de inicio no puede ser mayor a la fecha de fin."));
    }
    if (startDateParsed > dayjs(Date.now())) {
      newErrorTexts.push(t("La fecha de inicio no puede ser mayor a la fecha actual."));
    }
    if (endDateParsed > dayjs(Date.now())) {
      newErrorTexts.push(t("La fecha de fin no puede ser mayor a la fecha actual."));
    }

    const startInvalid =
      startDateParsed > endDateParsed || startDateParsed > dayjs(Date.now());
    const endInvalid =
      startDateParsed > endDateParsed || endDateParsed > dayjs(Date.now());
    setStartDateInput((prev) => ({ ...prev, invalid: startInvalid }));
    setEndDateInput((prev) => ({ ...prev, invalid: endInvalid }));
    setErrorTexts(newErrorTexts);
    return newErrorTexts.length === 0;
  }, [startDateInput?.value, endDateInput?.value, t]);

  useEffect(() => {
    if (interactedWith) validateDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateInput?.value, endDateInput?.value]);

  const handleDateChange = (setState, value) => {
    setInteractedWith(true);
    // Un input limpiado llega como "": preservarlo (formatearlo produciría el
    // string "Invalid Date", que pasaría el check de requerido).
    setState((prev) => ({ ...prev, value: value ? formatDateForInput(value) : "" }));
  };

  const toggleFormat = (format) => {
    setFormats((prev) =>
      prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]
    );
  };

  const chooseOrigin = (nextOrigin) => {
    if (nextOrigin === origin) return;
    setOrigin(nextOrigin);
    setFormats(nextOrigin === "pending" ? ["pdf", "zpl"] : ["pdf"]);
  };

  const close = () => {
    if (!isLoading) onClose();
  };

  // ── Origen: seleccionados en la tabla ─────────────────────────────────────
  const downloadSelectedBatch = async () => {
    if (!packages?.length) {
      showToast(t("Debe seleccionar al menos 1 paquete."), false);
      return;
    }
    setIsLoading(true);
    try {
      let downloadedAny = false;

      if (formats.includes("pdf")) {
        setProcessingStep(t("Generando etiquetas..."));
        // El PDF solo incluye paquetes con teléfono válido, así que el conteo
        // real de etiquetas y los omitidos se calculan con ese mismo filtro.
        const printableCount = packages.filter(hasValidReceiverPhone).length;
        const omittedCount = packages.length - printableCount;
        const { data, error } = await downloadPdf({
          clientId: user?.id,
          packages,
        });
        if (error || !data?.url) {
          showToast(apiBodyMessage(error) ?? t("Error al generar el PDF."), false);
        } else {
          window.open(data.url);
          downloadedAny = true;
          showToast(
            t("PDF generado ({{count}} etiquetas).", { count: printableCount }),
            true
          );
          if (omittedCount > 0) {
            showToast(
              t("{{filteredCount}} paquete(s) fueron omitidos por no tener número de teléfono válido", {
                filteredCount: omittedCount,
              }),
              "warning"
            );
          }
        }
      }

      const thermalChosen = THERMAL_FORMATS.filter((f) => formats.includes(f));
      if (thermalChosen.length > 0) {
        setProcessingStep(t("Generando etiquetas..."));
        const zplFinal = await multipleDownloadZPLFile({ packages, user });
        if (!zplFinal) {
          showToast(t("No se pudieron generar las etiquetas."), false);
        } else {
          thermalChosen.forEach((ext) => {
            const blob = new Blob([zplFinal], { type: "text/plain;charset=utf-8" });
            saveAs(blob, fileName(ext));
          });
          downloadedAny = true;
          showToast(
            t("Etiquetas {{ext}} generadas ({{count}}).", {
              ext: thermalChosen.map((e) => e.toUpperCase()).join(" + "),
              count: packages.length,
            }),
            true
          );
        }
      }

      if (downloadedAny && includePicking) {
        // Mismo lote que salió en las etiquetas: con PDF en juego aplica el
        // filtro de teléfono válido (el que usa formatPackagesPDFDownload).
        setProcessingStep(t("Generando picking & packing..."));
        const batch = formats.includes("pdf")
          ? packages.filter(hasValidReceiverPhone)
          : packages;
        await generatePickingPackingForBatch({ packages: batch, t });
      }

      if (downloadedAny) onClose();
    } catch (e) {
      console.error("Error al generar las etiquetas (seleccionados):", e);
      showToast(t("Error al generar las etiquetas."), false);
    } finally {
      setProcessingStep("");
      setIsLoading(false);
    }
  };

  // ── Origen: pendientes por rango y zona (la vieja "descarga masiva") ──────
  const downloadPendingBatch = async () => {
    if (!validateDates()) return;
    // Una vez que el PUT dejó los pedidos en status 100, cualquier fallo
    // posterior (PDF o térmica) tiene que decir que ya quedaron impresos: el
    // aviso del PDF no cubre ese caso por sí solo.
    let statusUpdated = false;
    let markedCount = 0;
    let printedWarningShown = false;
    setIsLoading(true);
    try {
      setProcessingStep(t("Obteniendo paquetes pendientes..."));
      const companyId = user?.companyId?._id;

      const params = {
        id: companyId,
        limit: 500,
        page: 1,
        query: { query: "", type: "" },
        orderField: "createdAt",
        status: [0],
        startDate: startDateInput?.value
          ? dayjs(startDateInput.value).format("MM/DD/YYYY HH:mm:ss")
          : null,
        endDate: endDateInput?.value
          ? dayjs(endDateInput.value).add(1, "days").format("MM/DD/YYYY HH:mm:ss")
          : null,
        // Se manda el NOMBRE de la región, no su código: el backend lo compara
        // contra `shipping_address.city.name`, que es donde el schema guarda la
        // región (la comuna va en `municipality.name`). `selectedRegion` es el
        // `code` porque es lo que necesita el fetch de comunas.
        region: selectedRegionName || null,
        coms: selectedCommune,
      };

      // `silent`: el toast de error lo da este flujo (uno solo, con el motivo
      // real); getAllPackages traería el suyo y se mostrarían dos.
      const response = await getAllPackages({ ...params, silent: true });
      if (!response.success || response.error) {
        console.error("Error en getAllPackages:", response.error);
        // Se lee el body del backend desde errorInfo.data para no perder el
        // motivo real y caer al fallback traducido cuando no hay ninguno.
        throw userFacingError(
          apiErrorMessage(response.error, t("Error al obtener los paquetes del servidor"))
        );
      }

      const docs = response.data?.docs;
      if (!docs || docs.length === 0) {
        showToast(
          t("No se encontraron etiquetas pendientes para el rango de fechas seleccionado"),
          "warning"
        );
        return;
      }

      setLabelsCount(docs.length);
      setProcessingStep(t("Actualizando estado de paquetes..."));

      const updateResponse = await updatePackagesStatus({
        ids: docs.map((pkg) => pkg._id),
        newStatus: 100,
      });
      // updatePackagesStatus devuelve `{ error: err.response.data }`: con un
      // 4xx/5xx de cuerpo vacío `error` es '' o undefined, así que el fallo se
      // detecta por la presencia de la clave y no por su valor.
      if (!updateResponse || "error" in updateResponse) {
        throw userFacingError(
          apiBodyMessage(updateResponse?.error) ??
            t("Error al actualizar el estado de los paquetes")
        );
      }
      statusUpdated = true;
      markedCount = docs.length;

      setProcessingStep(t("Generando etiquetas..."));

      // El lote imprimible excluye paquetes sin teléfono válido (mismo filtro
      // histórico para PDF y térmica en pendientes).
      const printableDocs = docs.filter(hasValidReceiverPhone);
      if (printableDocs.length === 0) {
        showToast(
          t("No hay paquetes válidos con número de teléfono para generar las etiquetas"),
          "warning"
        );
        return;
      }
      if (printableDocs.length < docs.length) {
        showToast(
          t("{{filteredCount}} paquete(s) fueron omitidos por no tener número de teléfono válido", {
            filteredCount: docs.length - printableDocs.length,
          }),
          "warning"
        );
      }

      const thermalChosen = THERMAL_FORMATS.filter((f) => formats.includes(f));
      // El POST del PDF corre DESPUÉS de marcar los pedidos como impresos
      // (status 100). Su fallo se captura en vez de romper el Promise.all: los
      // pedidos ya quedaron marcados y la térmica/picking igual pueden salir.
      const [pdfResult, zplContent] = await Promise.all([
        formats.includes("pdf")
          ? Axios.post("reports/pending-packages-pdf", {
              clientId: user?._id,
              index: 0,
              packages: formatPackagesPDFDownload(docs),
            }).catch((err) => ({ pdfError: err }))
          : Promise.resolve(null),
        thermalChosen.length > 0
          ? multipleDownloadZPLFile({ packages: printableDocs, user })
          : Promise.resolve(null),
      ]);

      setProcessingStep(t("Finalizando descarga..."));

      if (formats.includes("pdf")) {
        const pdfError = pdfResult?.pdfError;
        if (pdfError || !pdfResult?.data?.url) {
          printedWarningShown = true;
          showToast(
            t('Los {{n}} pedidos quedaron marcados como impresos, pero no se pudo generar el PDF: {{motivo}}. Usa "Seleccionados en la tabla" para reimprimirlos.', {
              n: markedCount,
              motivo: withoutTrailingPeriod(
                apiErrorMessage(pdfError, t("error desconocido"))
              ),
            }),
            false
          );
        } else {
          window.open(pdfResult.data.url);
        }
      }

      if (thermalChosen.length > 0) {
        if (!zplContent) {
          throw userFacingError(t("No se pudieron generar las etiquetas."));
        }
        thermalChosen.forEach((ext) => {
          const blob = new Blob([zplContent], { type: "text/plain;charset=utf-8" });
          saveAs(blob, fileName(ext));
        });
      }

      if (includePicking) {
        // Mismo lote que salió en las etiquetas. Contenida: el error jamás
        // debe impedir cerrar el modal.
        setProcessingStep(t("Generando picking & packing..."));
        await generatePickingPackingForBatch({ packages: printableDocs, t });
      }

      onClose();
    } catch (error) {
      console.error("Error en downloadPendingBatch:", error);
      if (statusUpdated && !printedWarningShown) {
        // Fallo con los pedidos ya marcados (térmica caída, error de JS, …):
        // se avisa que ya quedaron impresos para que no reintente a ciegas.
        showToast(
          t('Los {{n}} pedidos quedaron marcados como impresos, pero la descarga falló: {{motivo}}. Usa "Seleccionados en la tabla" para reimprimirlos.', {
            n: markedCount,
            motivo: withoutTrailingPeriod(
              error?.userFacing
                ? error.message
                : apiErrorMessage(error, t("error desconocido"))
            ),
          }),
          false
        );
      } else {
        showToast(
          error?.userFacing
            ? error.message
            : apiErrorMessage(error, t("Error al descargar etiquetas")),
          false
        );
      }
    } finally {
      setLabelsCount(null);
      setProcessingStep("");
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (origin === "pending") downloadPendingBatch();
    else downloadSelectedBatch();
  };

  const datesInvalid =
    origin === "pending" &&
    (errorTexts.length > 0 || !startDateInput?.value || !endDateInput?.value);
  const canDownload =
    formats.length > 0 &&
    !isLoading &&
    (origin === "selected" ? selectedCount > 0 : !statusDisabled && !datesInvalid);

  const originCards = [
    {
      id: "selected",
      icon: ListChecks,
      title: t("Seleccionados en la tabla"),
      subtitle:
        selectedCount > 0
          ? t("{{count}} pedidos marcados", { count: selectedCount })
          : t("No hay pedidos marcados en la tabla"),
      disabled: selectedCount === 0,
    },
    {
      id: "pending",
      icon: Clock,
      title: t("Pendientes por rango y zona"),
      subtitle: statusDisabled
        ? t("No disponible en esta vista")
        : t("Busca pedidos sin imprimir, descarga sus etiquetas y los marca como impresos."),
      disabled: statusDisabled,
    },
  ];

  const formatCards = [
    { id: "pdf", icon: FileText, label: "PDF", sub: t("Para imprimir en hoja") },
    { id: "zpl", icon: Printer, label: t("Térmica"), sub: t("Impresora Zebra · .zpl") },
    { id: "txt", icon: Type, label: t("Texto plano"), sub: t("Archivo .txt") },
  ];

  const footer =
    step === "origin" ? (
      <>
        <Button variant="outline" onClick={close} disabled={isLoading}>
          {t("Cancelar")}
        </Button>
        <Button onClick={() => setStep("options")} disabled={!origin}>
          {t("Continuar")}
        </Button>
      </>
    ) : (
      <>
        <Button
          variant="outline"
          className="mr-auto"
          onClick={() => setStep("origin")}
          disabled={isLoading}
        >
          {t("Atrás")}
        </Button>
        <Button
          onClick={handleDownload}
          disabled={!canDownload}
          loading={isLoading}
          iconLeft={<Download className="w-4 h-4" aria-hidden="true" />}
        >
          {origin === "selected"
            ? `${t("Descargar")} (${selectedCount})`
            : t("Descargar")}
        </Button>
      </>
    );

  return (
    <Modal
      open={isOpen}
      onClose={close}
      size="md"
      title={t("Descargar etiquetas")}
      description={
        step === "origin"
          ? t("Elige el origen de los pedidos")
          : origin === "selected"
          ? t("Seleccionados en la tabla")
          : t("Pendientes por rango y zona")
      }
      footer={footer}
    >
      {step === "origin" ? (
        <div className="space-y-2" role="radiogroup" aria-label={t("Elige el origen de los pedidos")}>
          {originCards.map(({ id, icon: Icon, title, subtitle, disabled }) => {
            const active = origin === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={disabled}
                onClick={() => chooseOrigin(id)}
                className={`w-full flex items-center gap-3 rounded-lg border p-3.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                  active
                    ? "border-brand-500 bg-brand-50/60"
                    : "border-border bg-bg hover:bg-bg-muted"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span
                  className={`w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-lg ${
                    active ? "bg-brand-500 text-white" : "bg-bg-muted text-fg-muted"
                  }`}
                >
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-fg">{title}</span>
                  <span className="block text-xs text-fg-muted mt-0.5">{subtitle}</span>
                </span>
                <span
                  aria-hidden="true"
                  className={`w-5 h-5 shrink-0 inline-flex items-center justify-center rounded-full border ${
                    active
                      ? "border-brand-500 bg-brand-500 text-white"
                      : "border-border text-transparent"
                  }`}
                >
                  <Check className="w-3 h-3" />
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="space-y-5">
          {origin === "pending" && (
            <>
              <section className="space-y-2.5">
                <h3 className="flex items-center gap-2 text-[0.6875rem] font-bold uppercase tracking-wider text-fg-muted">
                  <Calendar className="w-4 h-4" aria-hidden="true" />
                  {t("Rango de fechas")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="min-w-0 space-y-1">
                    <FieldLabel htmlFor="dlm-start-date">{t("Desde")}</FieldLabel>
                    <Input
                      id="dlm-start-date"
                      type="datetime-local"
                      invalid={startDateInput?.invalid}
                      onChange={(e) => handleDateChange(setStartDateInput, e.target.value)}
                      value={startDateInput?.value || ""}
                      aria-invalid={startDateInput?.invalid}
                    />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <FieldLabel htmlFor="dlm-end-date">{t("Hasta")}</FieldLabel>
                    <Input
                      id="dlm-end-date"
                      type="datetime-local"
                      invalid={endDateInput?.invalid}
                      onChange={(e) => handleDateChange(setEndDateInput, e.target.value)}
                      value={endDateInput?.value || ""}
                      aria-invalid={endDateInput?.invalid}
                    />
                  </div>
                </div>
                {errorTexts.length === 0 &&
                  (!startDateInput?.value || !endDateInput?.value) && (
                    <p className="text-xs text-fg-muted">
                      {t("Completa el rango de fechas para habilitar la descarga.")}
                    </p>
                  )}
              </section>

              <section className="space-y-2.5">
                <h3 className="flex items-center gap-2 text-[0.6875rem] font-bold uppercase tracking-wider text-fg-muted">
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  {t("Ubicación (opcional)")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="min-w-0 space-y-1">
                    <FieldLabel htmlFor="dlm-region">{t("Región")}</FieldLabel>
                    <Select
                      id="dlm-region"
                      value={selectedRegion || ""}
                      onChange={(e) => {
                        setSelectedRegion(e.target.value || null);
                        setSelectedCommune(null);
                        setCommunesList([]);
                      }}
                      disabled={isLoadingRegions}
                      placeholder={isLoadingRegions ? t("Cargando…") : t("Todas")}
                      options={regionsList}
                    />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <FieldLabel htmlFor="dlm-commune">{t("Comuna")}</FieldLabel>
                    <Select
                      id="dlm-commune"
                      value={selectedCommune || ""}
                      onChange={(e) => setSelectedCommune(e.target.value || null)}
                      disabled={isLoadingCommunes || !selectedRegion}
                      placeholder={
                        isLoadingCommunes
                          ? t("Cargando…")
                          : !selectedRegion
                          ? t("Elige región primero")
                          : t("Todas")
                      }
                      options={communesList}
                    />
                  </div>
                </div>
              </section>
            </>
          )}

          <section className="space-y-2.5">
            <h3 className="text-[0.6875rem] font-bold uppercase tracking-wider text-fg-muted">
              {t("Formato")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="group" aria-label={t("Formato")}>
              {formatCards.map(({ id, icon: Icon, label, sub }) => {
                const active = formats.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleFormat(id)}
                    className={`flex sm:flex-col items-center sm:items-start gap-2.5 sm:gap-1.5 rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                      active
                        ? "border-brand-500 bg-brand-50/60"
                        : "border-border bg-bg hover:bg-bg-muted"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 shrink-0 ${active ? "text-brand-700" : "text-fg-muted"}`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-fg">{label}</span>
                      <span className="block text-xs text-fg-muted">{sub}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {formats.length === 0 && (
              <p className="text-xs text-status-error" role="alert">
                {t("Elige al menos un formato.")}
              </p>
            )}
          </section>

          <button
            type="button"
            role="checkbox"
            aria-checked={includePicking}
            onClick={() => setIncludePicking((v) => !v)}
            className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              includePicking
                ? "border-brand-500 bg-brand-50/60"
                : "border-border bg-bg hover:bg-bg-muted"
            }`}
          >
            <ClipboardList
              className={`w-4 h-4 shrink-0 ${includePicking ? "text-brand-700" : "text-fg-muted"}`}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-fg">
                {t("Incluir hoja de picking & packing")}
              </span>
              <span className="block text-xs text-fg-muted">
                {t("Detalle de productos del lote, para armar los pedidos.")}
              </span>
            </span>
            <span
              aria-hidden="true"
              className={`w-5 h-5 shrink-0 inline-flex items-center justify-center rounded-md border ${
                includePicking
                  ? "border-brand-500 bg-brand-500 text-white"
                  : "border-border text-transparent"
              }`}
            >
              <Check className="w-3 h-3" />
            </span>
          </button>

          {origin === "pending" ? (
            <div className="flex items-start gap-2.5 rounded-lg border border-status-warning/40 bg-status-warning-soft p-3 text-xs leading-relaxed text-fg">
              <AlertTriangle className="w-4 h-4 shrink-0 text-status-warning mt-px" aria-hidden="true" />
              <span>{t("Los pedidos encontrados se marcarán como impresos (listos para enviar).")}</span>
            </div>
          ) : (
            formats.includes("pdf") && (
              <div className="flex items-start gap-2.5 rounded-lg bg-bg-muted p-3 text-xs leading-relaxed text-fg-muted">
                <Info className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
                <span>{t("El PDF marca los pedidos como impresos (listos para enviar).")}</span>
              </div>
            )
          )}

          {origin === "pending" && errorTexts.length > 0 && (
            <div
              className="space-y-1.5 rounded-lg border border-status-error/40 bg-status-error-soft p-3"
              role="alert"
              aria-live="polite"
            >
              {errorTexts.map((errorText, index) => (
                <div key={index} className="flex items-center gap-2 text-xs text-status-error">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  <span>{errorText}</span>
                </div>
              ))}
            </div>
          )}

          {isLoading && processingStep && (
            <div
              className="flex items-center gap-3 rounded-lg bg-bg-muted p-3"
              role="status"
              aria-live="polite"
            >
              <span
                aria-hidden="true"
                className="w-5 h-5 shrink-0 rounded-full border-2 border-border border-t-brand-500 animate-spin"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-fg">{processingStep}</span>
                {labelsCount && (
                  <span className="block text-xs text-fg-muted">
                    {t("Procesando")} {labelsCount} {t("etiquetas…")}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DownloadLabelsModal;
